import { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";

import {
  ArrowRightIcon,
  AudienceIcon,
  Box,
  Button,
  ButtonGroup,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  Column,
  Combobox,
  DragIcon,
  GroupedCombobox,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Radio,
  RadioGroup,
  Row,
  Select,
  SettingsIcon,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  TraitIcon,
  useDisclosure,
  WandIcon,
} from "@hightouch/htui";
import { MATCH_BOOSTER_TARGET_LEVEL_CONFIG_KEY } from "@hightouch/lib/match-booster/consts";
// Import the narrow module (not the `interfaces/destination` barrel): the barrel
// transitively pulls Node-only deps (minipass/tar) into the browser bundle.
// `mapping.ts` has only `import type` deps, so it's browser-safe.
import { captureException } from "src/lib/monitoring";
import { useFlags } from "launchdarkly-react-client-sdk";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import {
  Controller,
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { isPresent } from "ts-extras";

import { usePermissionContext } from "src/components/permission/permission-context";
import { useDestinationForm } from "src/contexts/destination-form-context";
import { useMatchboostingStatsQuery } from "src/graphql";
import { flattenOptions } from "src/ui/select";
import { automap, suggest } from "src/utils/automap";
import { NEW_ICON_MAP } from "src/utils/destinations";
import { HiddenFormLabel } from "src/components/form";
import {
  canSelectHouseholdTargeting,
  getEnabledMatchBoosterRegionsForModel,
  getMatchBoosterRegionSettings,
  resolveMatchBoosterRegion,
} from "src/utils/match-booster-regions";

import {
  ExtendedOption,
  ExtendedTypesSync,
  fromExtendedOptiontoOption,
  Option,
} from "@hightouch/formkit";
import { Reorder, useDragControls } from "motion/react";
import { useUser } from "src/contexts/user-context";
import { useMatchBoosterEnabled } from "src/hooks/use-match-booster-enabled";
import {
  ColumnOption,
  useFormkitContext,
} from "src/formkit/components/formkit-context";
import { SyncType } from "src/formkit/components/sync-type";
import {
  JsonColumnProps,
  MappingType,
  SelectedOptionContext,
  SpreadTransform,
  SpreadTransformSide,
  SpreadTransformType,
  spreadTransformOptions,
  spreadFieldName,
} from "src/formkit/components/types";
import {
  isMatchBoosterSetUpToUse,
  isMatchBoostingAvailableForSyncConfig,
  isMatchboostingEnabled,
  resolveFromValue,
} from "src/formkit/components/utils";
import { useParams } from "src/router";
import { isColumnReference } from "src/types/visual";
import { ArrayInlineMapper } from "./array-inline-mapper";
import { AssociationMapper, AssociationOptions } from "./association-mapper";
import { InlineMapper } from "./inline-mapper";
import { Mapper } from "./mapper";
import { MappingsHeader } from "./mappings-header";
import { MatchBoosterMappingToggle, TargetingLevel } from "./matchbooster";
import { TypeSelect } from "./type-select";
import { GetIsValidProps } from "./types";
import {
  destinationOptionKey,
  enforceMappingType,
  extractEligibleInlineMapperColumns,
  findSelectedDestinationOption,
  isValidJsonColumnMetadata,
  mappingDestinationKey,
} from "./utils";
import { useManageAutoSyncColumnsState } from "./hooks/use-manage-auto-sync-columns-state";

type Props = {
  name: string;
  options?: ExtendedOption[];
  customColumns?: ColumnOption[];
  asyncOptions?: boolean;
  isDisabled?: boolean;
  loading?: boolean;
  error?: string;
  required?: boolean;
  creatable?: boolean;
  /** `creatableTypes` are the types the user can select from when creating their own field. */
  creatableTypes?: ExtendedOption[];
  autoSyncColumnsDefault?: boolean;
  excludeMappings?: string[];
  reload?: () => void;
  advanced?: boolean;
  associationOptions?: AssociationOptions;
  templates?: { name: string; description: string; placeholders?: string[] }[];

  allEnabled?: boolean;
  allEnabledKey?: string;
  allEnabledLabel?: string;
  enableInLineMapper?: boolean;
  componentSupportsMatchBoosting?: boolean;
  matchboosterSemanticColumnsToMap?: string[];

  allowCreatableAutomapperWithFields?: string[];
  allowIgnoreNullForAssociations?: boolean;
  supportOnlyWriteIfNull?: boolean;
  spread?: boolean;
  mappingTypes?: MappingType[];
};

type OptionChangeArgs = {
  destinationFieldOptions: ExtendedOption[] | undefined;
  data: any;
  value: string | undefined;
  isCreatedOption?: boolean;
  typeOptions?: Record<string, boolean | number | string>;
};

export const formatOptionLabel = (object) => {
  return (
    object.label + (object?.object?.label ? ` (${object.object.label})` : "")
  );
};

const clearMappingErrors = (
  errors: Error,
  index: number[],
  setErrors: (error: Error) => void,
): void => {
  const updatedErrors = { ...errors };

  index.forEach((i) => {
    const errorKeys = [
      `mappings[${i}].from`,
      `mappings[${i}].to`,
      `mappings[${i}].lookup.from`,
      `mappings[${i}].lookup.by`,
    ];

    errorKeys.forEach((key) => {
      if (updatedErrors[key]) {
        delete updatedErrors[key];
      }
    });
  });

  setErrors(updatedErrors);
};

const retrieveErrorMessage = (
  errors: any,
  index: number,
  name: string,
): string => {
  const fromError = errors?.[`${name}[${index}].from`];
  const toError = errors?.[`${name}[${index}].to`];
  if (fromError || toError) {
    for (const direction of ["from", "to"]) {
      const mappingPath = `${name}[${index}].${direction}`;
      const errorMessage = errors?.[mappingPath];
      if (typeof errorMessage === "string") {
        return errorMessage.replace(mappingPath, "This");
      }
    }
  }

  const byAssociationError = errors?.[`mappings[${index}].lookup.by`];
  const fromAssociationError = errors?.[`mappings[${index}].lookup.from`];
  if (byAssociationError || fromAssociationError) {
    for (const direction of ["by", "from"]) {
      const mappingPath = `${name}[${index}].lookup.${direction}`;
      const errorMessage = errors?.[mappingPath];
      if (typeof errorMessage === "string") {
        return errorMessage.replace(mappingPath, "This");
      }
    }
  }
  return "";
};

export const Mappings: React.FC<Readonly<Props>> = ({
  name,
  options,
  customColumns,
  required,
  loading,
  reload,
  creatable,
  creatableTypes,
  allEnabled: _allEnabled,
  allEnabledLabel,
  allEnabledKey,
  autoSyncColumnsDefault = false,
  excludeMappings = [],
  advanced,
  associationOptions,
  templates,
  enableInLineMapper,
  componentSupportsMatchBoosting,
  matchboosterSemanticColumnsToMap,
  allowCreatableAutomapperWithFields,
  allowIgnoreNullForAssociations,
  supportOnlyWriteIfNull,
  spread,
  mappingTypes,
  isDisabled = false,
}) => {
  const {
    model,
    destination,
    destinationDefinition,
    sourceDefinition,
    columns: allColumns,
    supportsMatchboosting,
  } = useFormkitContext();
  // For realtime audiences, always hide the option to map all columns.
  // For all other models, use the provided prop value from the form definition.
  const allEnabled = model?.is_realtime_audience ? false : _allEnabled;
  const columns = customColumns ?? allColumns;
  const [jsonColumnProperties, setJsonColumnProperties] =
    useState<JsonColumnProps>({
      selectedColumnProps: undefined,
      allColumnsProps: undefined,
    });

  const { workspace } = useUser();
  const {
    config,
    errors,
    hasDependentSyncRun,
    matchBoosterRegionControl,
    selectedMatchBoosterRegion,
    setErrors,
    sync,
  } = useDestinationForm();
  const { id: sync_template_id } = useParams<{ id?: string }>();
  const { watch, getValues, setValue } = useFormContext();
  const [movedItemId, setMovedItemId] = useState<string | null>(null);
  const { fields, append, move, remove, replace } = useFieldArray({
    name,
  });

  // We want to get the last enrichment stats to confirm it's okay to display
  // boosted columns
  const enabledMatchBoosterRegions = model
    ? getEnabledMatchBoosterRegionsForModel(model)
    : [];
  const matchBoosterRegion = resolveMatchBoosterRegion({
    selectedRegion: selectedMatchBoosterRegion,
    savedRegion: sync?.matchbooster_region,
    enabledRegions: enabledMatchBoosterRegions,
  });
  const matchBoosterRegionSettings = getMatchBoosterRegionSettings(
    (model?.parent ?? model)?.matchbooster_settings,
    matchBoosterRegion,
  );
  const householdTargetingEnabled = canSelectHouseholdTargeting({
    region: matchBoosterRegion,
    useHouseholdTargeting:
      matchBoosterRegionSettings.useHouseholdTargeting === true,
  });
  const { data: modelLastEnrichedStats } = useMatchboostingStatsQuery(
    {
      // use the parent model id if this an audience, otherwise the model id.
      modelId: model?.parent?.id || model?.id,
      region: matchBoosterRegion,
    },
    {
      enabled: Boolean(model?.id),
      select: (data) => data.matchboosting_stats[0],
    },
  );

  const autoSyncKey = allEnabledKey ?? "autoSyncColumns";
  const watchAutoSyncColumns = useWatch({ name: autoSyncKey });
  const watchFieldArray = useWatch({ name });

  const [isSuggested, setIsSuggested] = useState(false);
  const { appEnableInLineMapper } = useFlags();

  const mbEnabledForWorkspace = useMatchBoosterEnabled();

  const mbTargetingLevel = watch(MATCH_BOOSTER_TARGET_LEVEL_CONFIG_KEY) as
    | TargetingLevel
    | undefined;

  const onMbTargetingLevelChange = (level: TargetingLevel) => {
    setValue(MATCH_BOOSTER_TARGET_LEVEL_CONFIG_KEY, level, {
      shouldDirty: true,
    });
  };

  // The EU graph is person-only, so a region switch to EU can leave a
  // household selection stranded. Reset it rather than let the sync save
  // with a targeting level the resolved region can't serve.
  useEffect(() => {
    if (
      matchBoosterRegion === "eu" &&
      mbTargetingLevel === TargetingLevel.Household
    ) {
      setValue(MATCH_BOOSTER_TARGET_LEVEL_CONFIG_KEY, TargetingLevel.Person, {
        shouldDirty: true,
      });
    }
  }, [matchBoosterRegion, mbTargetingLevel, setValue]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!watchFieldArray) {
      setValue(name, []);
    }
  }, [watchFieldArray]);

  const controlledFields = useMemo(() => {
    return (
      fields?.map((field, index) => {
        return {
          id: field.id,
          ...watchFieldArray?.[index],
        };
      }) || []
    );
  }, [fields, watchFieldArray]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    const subscription = watch((state, { name: key }) => {
      const currentMappings = state[name];

      // handles when externalIdMapping overlaps with current component
      if (currentMappings && key && excludeMappings.includes(key)) {
        const value = state[key];
        let otherMappings: any[] = [];
        if (Array.isArray(value)) {
          otherMappings = value;
        } else if (typeof value === "object") {
          otherMappings = [value];
        }

        const fieldsWithoutExcluded = currentMappings.filter(
          (currentMapping) =>
            !otherMappings.some(
              (otherMapping) =>
                currentMapping.to === otherMapping.to &&
                isPresent(currentMapping.to) &&
                isPresent(otherMapping.to),
            ),
        );

        if (!isEqual(currentMappings, fieldsWithoutExcluded)) {
          setValue(name, fieldsWithoutExcluded);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [excludeMappings, watch]);

  const excludedFields: { to: unknown }[] = [];

  for (const key of excludeMappings) {
    const watchMapping: unknown = watch(key);
    if (Array.isArray(watchMapping)) {
      excludedFields.push(...watchMapping);
    } else if (
      typeof watchMapping === "object" &&
      watchMapping !== null &&
      "to" in watchMapping
    ) {
      excludedFields.push(watchMapping);
    }
  }

  // Create a memoized version of isOptionExcluded to avoid dependency cycles
  const isOptionExcluded = useMemo(
    () => (option) => {
      if (option.disabled) {
        return true;
      }
      const valueAlreadyMapped = controlledFields.some(
        ({ to }) =>
          to ===
          (isColumnReference(option.value) ? option.label : option.value),
      );
      const usedInOtherMappings = excludedFields.some(
        ({ to }) =>
          to ===
          (isColumnReference(option.value) ? option.label : option.value),
      );

      return valueAlreadyMapped || usedInOtherMappings;
    },
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    [controlledFields, excludedFields],
  );

  // Add in required options
  const requiredOptionsNotPresent = useMemo(
    () =>
      options?.filter(
        (option) =>
          (option.required ?? option.extendedType?.required) &&
          !isOptionExcluded(option),
      ),
    [options, isOptionExcluded],
  );

  useEffect(() => {
    // Remove options that may not be available now or possible duplicates (due to modifiers)
    const existingMappings: string[] = [];
    const indexesToRemove: number[] = [];
    controlledFields?.forEach(({ to }, index) => {
      // Remove any duplicate mappings (from options having required again)
      if (to && existingMappings.includes(to) && options) {
        indexesToRemove.push(index);
      }
      existingMappings.push(to);
    });
    if (indexesToRemove.length > 0) {
      remove(indexesToRemove);
    }
  }, [controlledFields, options, remove]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (requiredOptionsNotPresent?.length) {
      // Don't reset if the required options are already present in controlled fields
      const currentMappedFields = getValues(name) ?? [];
      const fieldsToMap = requiredOptionsNotPresent.filter(
        (r) => !currentMappedFields.some((field) => field.to === r.value),
      );

      if (fieldsToMap.length > 0) {
        const requiredMappings = [
          ...currentMappedFields,
          ...fieldsToMap.map((r) => {
            // Map references differently
            if (r?.extendedType?.type === "REFERENCE") {
              return {
                to: r.value,
                type: "reference",
                lookup: {
                  object: r.objectType || r.value,
                },
                object: r["object"]?.value,
              };
            }

            return {
              to: r.value,
              type: "standard",
              object: r["object"]?.value,
            };
          }),
        ];

        setValue(name, requiredMappings);
      }
    }
  }, [requiredOptionsNotPresent]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!requiredOptionsNotPresent?.length && !controlledFields.length) {
      setValue(name, [{ type: "standard" }]);
    }
  }, [requiredOptionsNotPresent, controlledFields.length, name]);

  const suggestColumns = () => {
    let automapOptions = options;
    if (options?.length && allowCreatableAutomapperWithFields) {
      // If all available destination fields are included in the `allowCreatableAutomapperWithFields`, add model columns
      // to be auto suggested
      const fromExtendedOptions: Option[] = options.map(
        fromExtendedOptiontoOption,
      );
      const shouldAddModelColumns = fromExtendedOptions.every((option) =>
        allowCreatableAutomapperWithFields.includes(option.value as string),
      );
      if (shouldAddModelColumns) {
        const flatColumns = flattenOptions(columns);
        const destinationFields = fromExtendedOptions.map(
          (option) => option.value as string,
        );

        // Only add the model columns if destination fields don't have the value already
        const filtered = flatColumns.filter(
          (column) =>
            !destinationFields.find((field) => field === column.value),
        );
        automapOptions = [...options, ...filtered];
      }
    }

    const unmappedOptions = getUnmappedColumns(
      columns.filter(
        (columnGroup) => !columnGroup.entity?.isJourneyContextVariable,
      ),
      automapOptions,
    );
    const automappedColumns = automap(columns, unmappedOptions);
    setIsSuggested(true);
    return automappedColumns;
  };

  function getUnmappedColumns(
    columns: ColumnOption[],
    options: ExtendedOption[] | undefined,
  ) {
    const flatColumns = flattenOptions(columns);
    return (
      !options?.length ? flatColumns : options?.map(fromExtendedOptiontoOption)
    )?.filter((option) => !isOptionExcluded(option));
  }

  const mbAvailableForSyncConfig = isMatchBoostingAvailableForSyncConfig({
    options,
    componentSupportsMatchBoosting,
    destinationSupportsMatchBoosting: supportsMatchboosting,
  });

  const matchBoostingEnabled = isMatchboostingEnabled({ fields });

  const onToggleMatchboosterEnabled = () => {
    if (matchBoostingEnabled) {
      const indexesToRemove: number[] = [];
      controlledFields.forEach((field, index) => {
        if (field?.from?.type === "boosted") {
          indexesToRemove.push(index);
        }
      });
      if (indexesToRemove.length > 0) {
        remove(indexesToRemove);
      }
      return;
    }

    // Some destinations don't have boosted fields (i.e. BigQuery ADH), since
    // the fields are user-provided and we don't know their semantic meaning.
    // In these cases, we provide the mapping via the matchboosterSemanticColumnsToMap
    // on the column semantic types.
    if (matchboosterSemanticColumnsToMap?.length) {
      const boostedColumns = columns.find(
        (columns) => columns.label === "boosted",
      );
      if (!boostedColumns) {
        // This should never happen, it means that matchboosting isn't
        // enabled for this destination.
        captureException(new Error("Could not find boosted columns section"));
        return;
      }

      const boostedOptions = boostedColumns.options?.filter((option) =>
        matchboosterSemanticColumnsToMap.includes(
          (option.value as any).semanticType,
        ),
      );

      if (!boostedOptions?.length) {
        // This should never happen - this would mean the destination
        // hasn't implemented the matchboosterSemanticColumnsToMap.
        captureException(new Error("Could not find columns to boost"));
        return;
      }

      const fieldsToBoost = boostedOptions.map((option) => {
        return {
          from: option.value,
          to: null,
        };
      });

      append(fieldsToBoost);
      return;
    }

    const boostedFields =
      options?.filter((option) => !!option?.extendedType?.semanticColumnType) ??
      [];

    // Replace the mappings fields with the boosted fields first and then the rest of the mapped fields.
    // Remove any empty or duplicated (with the boosted fields) mappings that were added before toggling.
    const updatedControlledFields = [
      ...(boostedFields.map((field) => {
        return {
          from: {
            type: "boosted",
            semanticType: field?.extendedType?.semanticColumnType,
          },
          to: field.value,
        };
      }) || []),
      ...controlledFields.filter(
        (field) =>
          (field.from || field.to) &&
          !boostedFields.some(
            (boostedField) => boostedField.value === field.to,
          ),
      ),
    ];

    if (updatedControlledFields.length > 0) {
      replace(updatedControlledFields);
    }
  };

  const mbSetUpToUse = isMatchBoosterSetUpToUse({
    mbEnabledForWorkspace: mbEnabledForWorkspace,
    model,
    lastEnrichedAt: modelLastEnrichedStats?.last_enriched_at,
  });
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    // Default to enable MB, if available and user has MB set up when creating a sync
    if (
      mbAvailableForSyncConfig &&
      mbSetUpToUse &&
      !matchBoostingEnabled &&
      !sync?.id &&
      !sync_template_id
    ) {
      onToggleMatchboosterEnabled();
    }
  }, [sync, mbAvailableForSyncConfig, mbSetUpToUse]);

  const handleOptionChange = ({
    destinationFieldOptions,
    data,
    value,
    isCreatedOption,
    typeOptions,
  }: OptionChangeArgs): void => {
    // Combobox selection sends a composite (object + value) key so two options
    // that share a value but differ by object can be told apart. The TypeSelect
    // "create" path (isCreatedOption) still sends the raw fieldType value.
    const option = isCreatedOption
      ? destinationFieldOptions?.find((option) => option.value === value)
      : destinationFieldOptions?.find(
          (option) => destinationOptionKey(option) === value,
        );

    const newFieldData = { ...data.field.value, object: option?.object?.value };
    if (isCreatedOption) {
      newFieldData.fieldType = value;
      newFieldData.typeOptions = typeOptions;
    } else {
      newFieldData.to = option?.value;
    }

    if (option?.extendedType?.type === "REFERENCE") {
      newFieldData.type = "reference";
      newFieldData.lookup = {
        object:
          option?.referenceObjects?.[0]?.value ||
          option?.objectType ||
          option?.value,
        from: data.field.value?.from,
      };
    } else if (
      option?.extendedType?.type === "OBJECT" ||
      option?.extendedType?.type === "ARRAY"
    ) {
      if (enableInLineMapper && appEnableInLineMapper) {
        newFieldData.from = undefined;
        newFieldData.type =
          option?.extendedType?.type === "OBJECT"
            ? MappingType.OBJECT
            : MappingType.ARRAY;
      }
    } else if (data.field.value?.type === "reference") {
      newFieldData.type = "standard";
      newFieldData.from = data.field.value?.lookup?.from;
    }

    // These destinations use custom logic in the mappings to handle multiple objects and do not conform to the standard mapping types
    if (["salesforce", "salesforceSandbox"].includes(destination?.type || "")) {
      data.field.onChange(newFieldData);
    } else {
      data.field.onChange(enforceMappingType(newFieldData));
    }
  };

  const permission = usePermissionContext();

  const columnOptionGroups = useMemo(() => {
    return columns.map((optionGroup) => ({
      ...optionGroup,
      options: optionGroup.options ?? [],
    }));
  }, [columns]);

  const MAPPING_TYPE_OPTIONS = [
    {
      label: allEnabledLabel || "Sync all columns",
      value: true,
    },
    {
      label: "Sync specific columns",
      value: false,
    },
  ];

  /**
   * This hook manages the autoSyncColumnsKey in the react-hook-form state.
   *
   * autoSyncColumnsKey is **not** set by the formkit definition.
   */
  useManageAutoSyncColumnsState({
    allEnabled,
    autoSyncColumnsDefault,
    autoSyncColumnsKey: autoSyncKey,
    numberOfMappingsApplied: controlledFields.length,
    mappingsFieldName: name,
    onResetMappings: () => {
      replace([]);
    },
  });

  if (!watchFieldArray) {
    return null;
  }

  if (allEnabled && watchAutoSyncColumns) {
    return (
      <Controller
        name={autoSyncKey}
        render={({ field }) => {
          return (
            <RadioGroup
              isDisabled={isDisabled || permission.unauthorized}
              orientation="vertical"
              value={Boolean(field.value)}
              onChange={(value) => {
                field.onChange(value);
              }}
            >
              {MAPPING_TYPE_OPTIONS.map((option, index) => (
                <Radio key={index} label={option.label} value={option.value} />
              ))}
            </RadioGroup>
          );
        }}
      />
    );
  }

  const loadEligibleInlineMapperColumns = async (
    currentSelectedColumn?: string,
  ): Promise<void> => {
    const allColumnsProps = extractEligibleInlineMapperColumns(columns);
    if (isValidJsonColumnMetadata(allColumnsProps)) {
      setJsonColumnProperties((currentValue) => ({
        selectedColumnProps: currentSelectedColumn
          ? allColumnsProps[currentSelectedColumn]
          : currentValue.selectedColumnProps,
        allColumnsProps,
      }));
    }
  };

  const getIsValid = ({
    errors,
    index,
    mappingDirection,
    mappingName,
  }: GetIsValidProps): boolean => {
    const error = errors?.[`${mappingName}[${index}].${mappingDirection}`];
    return Boolean(error);
  };

  // reorder field in the list based on the drag and drop
  const reorder = (updatedList: any[]): void => {
    const startIndex = controlledFields.findIndex(
      (rule) => rule.id === movedItemId,
    );
    const destinationIndex = updatedList.findIndex(
      (row) => row.id === movedItemId,
    );

    // clear the error if there is one
    clearMappingErrors(errors, [destinationIndex], setErrors);

    move(startIndex, destinationIndex);
  };

  const isRealtimeAudience = Boolean(model?.is_realtime_audience);

  return (
    <Column aria-label={`${name} mappings`} gap={2}>
      {allEnabled && (
        <Controller
          name={autoSyncKey}
          render={({ field }) => {
            return (
              <RadioGroup
                isDisabled={isDisabled || permission.unauthorized}
                mb={4}
                orientation="vertical"
                value={Boolean(field.value)}
                onChange={(value) => {
                  field.onChange(value);
                }}
              >
                {MAPPING_TYPE_OPTIONS.map((option, index) => (
                  <Radio
                    key={index}
                    label={option.label}
                    value={option.value}
                  />
                ))}
              </RadioGroup>
            );
          }}
        />
      )}
      <MatchBoosterMappingToggle
        isDisabled={isDisabled || permission.unauthorized}
        mbAvailableForSyncConfig={mbAvailableForSyncConfig}
        mbEnabledForWorkspace={mbEnabledForWorkspace}
        matchBoostingEnabled={matchBoostingEnabled}
        matchBoosterRegionControl={matchBoosterRegionControl}
        onToggle={onToggleMatchboosterEnabled}
        model={model}
        workspaceId={workspace?.id}
        syncId={sync?.id}
        config={config}
        destinationDefinition={destinationDefinition}
        householdTargetingEnabled={householdTargetingEnabled}
        matchBoosterRegion={matchBoosterRegion}
        syncRan={Boolean(sync?.last_run_at)}
        hasDependentSyncRun={hasDependentSyncRun}
        targetingLevel={mbTargetingLevel}
        onTargetingLevelChange={onMbTargetingLevelChange}
      />
      {controlledFields?.length > 0 && (
        <Box
          alignItems="center"
          display="grid"
          gap={4}
          gridTemplateColumns="minmax(0, 1fr) 32px minmax(0, 1fr) 24px 24px"
        >
          <MappingsHeader
            isDisabled={isDisabled || permission.unauthorized}
            loading={loading}
            reload={reload}
            usingCustomFromOptions={Boolean(customColumns)}
          />
        </Box>
      )}
      <Reorder.Group
        layoutScroll
        style={{
          listStyle: "none",
          paddingLeft: 0,
          paddingBottom: "var(--chakra-space-4)",
          position: "relative",
          display: "grid",
          gridGap: "var(--chakra-space-1)",
        }}
        values={controlledFields}
        onReorder={reorder}
      >
        {controlledFields.map((field, index) => {
          const isDragDisabled =
            isDisabled ||
            permission.unauthorized ||
            watchAutoSyncColumns ||
            loading;

          return (
            <DraggableItem
              key={field.id}
              isDownDisabled={
                isDragDisabled || index === controlledFields.length - 1
              }
              isDragDisabled={isDragDisabled || controlledFields.length === 1}
              isUpDisabled={isDragDisabled || index === 0}
              item={field}
              onDown={async () => {
                // clear the error if there is one
                clearMappingErrors(errors, [index, index + 1], setErrors);
                move(index, index + 1);
              }}
              onDragEnd={() => {
                setMovedItemId(null);
              }}
              onDragStart={(id) => {
                // clear the error if there is one
                clearMappingErrors(errors, [index], setErrors);
                setMovedItemId(id);
              }}
              onUp={async () => {
                // clear the error if there is one
                clearMappingErrors(errors, [index, index - 1], setErrors);
                move(index, index - 1);
              }}
            >
              <Controller
                name={`${name}.${index}`}
                render={(data) => {
                  const isMatchboosterField =
                    data.field.value?.from?.type === "boosted";
                  const destinationFieldOptions = options?.map((option) => {
                    if (isOptionExcluded(option)) {
                      return { ...option, disabled: true };
                    } else {
                      return option;
                    }
                  });
                  const selectedOption = findSelectedDestinationOption(
                    options,
                    data.field.value,
                  );
                  // Composite identity of the current selection, so the Combobox
                  // can match the right one of two same-value options. Derive it
                  // from the resolved option when possible: a legacy mapping with
                  // no persisted object still resolves (value-only fallback) to an
                  // object-bearing option, and its key must match that option's.
                  const selectedOptionKey = selectedOption
                    ? destinationOptionKey(selectedOption)
                    : data.field.value?.to == null
                      ? undefined
                      : mappingDestinationKey(data.field.value);

                  const allDestinationOptions = [
                    ...(destinationFieldOptions ?? []),
                  ];

                  // If mapping destination is configured, but it's not in the list of
                  // available options, it's either a custom value typed by the user or
                  // it comes from a suggested mapping
                  if (
                    typeof data.field.value?.to === "string" &&
                    !selectedOption
                  ) {
                    allDestinationOptions.push({
                      label: data.field.value.to,
                      value: data.field.value.to,
                      disabled: false,
                    });
                  }

                  const isMappingRequired = Boolean(
                    selectedOption?.required ??
                      selectedOption?.extendedType?.required,
                  );
                  const referenceObjectOptions =
                    selectedOption?.referenceObjects;

                  const optionContext = extendedTypeToFormkitOptions(
                    data.field?.value?.fieldType && creatableTypes
                      ? creatableTypes.find(
                          (option) =>
                            option.value === data.field.value.fieldType,
                        )
                      : selectedOption,
                  );

                  return (
                    <Box
                      alignItems="center"
                      display="grid"
                      gap={1}
                      gridTemplateColumns="minmax(0, 1fr) 32px minmax(0, 1fr) 24px 24px"
                      mb={
                        data.field.value?.to &&
                        data.field.value?.typeOptions &&
                        "1rem"
                      }
                    >
                      {data.field.value?.type === "reference" ? (
                        <AssociationMapper
                          associationOptions={associationOptions}
                          isDisabled={isDisabled || permission.unauthorized}
                          index={index}
                          name={name}
                          value={data.field.value}
                          onChange={data.field.onChange}
                        />
                      ) : advanced ? (
                        <Mapper
                          enableInLineMapper={
                            enableInLineMapper && appEnableInLineMapper
                          }
                          isDisabled={isDisabled || permission.unauthorized}
                          isError={getIsValid({
                            errors,
                            index,
                            mappingDirection: "from",
                            mappingName: name,
                          })}
                          isRealtimeSync={isRealtimeAudience}
                          jsonColumnProperties={jsonColumnProperties}
                          mappingTypes={mappingTypes}
                          placeholder={`Select a field from ${sourceDefinition?.name ?? "the source"}`}
                          selectedOption={selectedOption}
                          templates={templates ?? []}
                          value={data.field.value}
                          onReloadEligibleInlineMapperColumns={
                            loadEligibleInlineMapperColumns
                          }
                          onChange={(value) => {
                            // For destinations like Iterable where fields can be created dynamically
                            // but no predefined options exist, auto-map the source field name to destination
                            if (
                              !data.field.value?.to &&
                              value.type === MappingType.STANDARD &&
                              creatable &&
                              !destinationFieldOptions?.length
                            ) {
                              const fieldName =
                                value.alias ?? resolveFromValue(value.from);
                              data.field.onChange({
                                from: value.from,
                                to: fieldName,
                                type: value.type,
                              });
                              return;
                            }

                            const availableOptions = getUnmappedColumns(
                              columns,
                              destinationFieldOptions,
                            );
                            if (
                              !data.field.value?.to &&
                              availableOptions?.length &&
                              value.type === MappingType.STANDARD
                            ) {
                              // If there is an alias for the column, and fields are creatable,
                              // use the alias as the new field name
                              if (value.alias && creatable) {
                                data.field.onChange({
                                  from: value.from,
                                  to: value.alias,
                                  type: value.type,
                                });
                                return;
                              }

                              // Ensure that the fieldName is used for the label property since FuzzySet methods (used in automap.ts)
                              // require string arguments.
                              const fieldName =
                                value.alias ?? resolveFromValue(value.from);

                              data.field.onChange(
                                suggest(
                                  {
                                    label: fieldName,
                                    value: value.from,
                                  },
                                  availableOptions,
                                ),
                              );
                            } else {
                              data.field.onChange({
                                to: data.field.value?.to,
                                object: data.field.value?.object,
                                ...value,
                              });
                            }
                          }}
                          onChangeJsonColumnProperties={setJsonColumnProperties}
                        />
                      ) : (
                        <HiddenFormLabel
                          label={`Select a field from ${sourceDefinition?.name ?? "the source"}`}
                        >
                          <GroupedCombobox
                            isDisabled={isDisabled || permission.unauthorized}
                            isInvalid={getIsValid({
                              errors,
                              index,
                              mappingName: name,
                              mappingDirection: "from",
                            })}
                            optionAccessories={(option) => {
                              return option?.value?.["type"] ===
                                "additionalColumnReference"
                                ? {
                                    leftAccessory: {
                                      type: "icon",
                                      icon: TraitIcon,
                                    },
                                  }
                                : undefined;
                            }}
                            optionGroups={columnOptionGroups}
                            placeholder={
                              data.field.value?.to
                                ? ""
                                : `Select a field from ${sourceDefinition?.name ?? "the source"}`
                            }
                            value={data.field.value?.from}
                            onChange={(value) => {
                              let option: ColumnOption | undefined;

                              // Find an original option object for a selected value
                              for (const optionGroup of columnOptionGroups) {
                                for (const optionGroupItem of optionGroup.options) {
                                  if (optionGroupItem.value === value) {
                                    option = optionGroupItem;
                                    break;
                                  }
                                }
                              }

                              // For destinations like Iterable where fields can be created dynamically
                              // but no predefined options exist, auto-map the source field name to destination
                              if (
                                !data.field.value?.to &&
                                option &&
                                creatable &&
                                !destinationFieldOptions?.length
                              ) {
                                const fieldName =
                                  option.suggestedName ??
                                  option.label ??
                                  resolveFromValue(option.value);
                                data.field.onChange({
                                  from: option.value,
                                  to: fieldName,
                                  type: MappingType.STANDARD,
                                });
                                return;
                              }

                              const availableOptions = getUnmappedColumns(
                                columns,
                                destinationFieldOptions,
                              );
                              if (!data.field.value?.to && option) {
                                data.field.onChange(
                                  suggest(option as any, availableOptions),
                                );
                              } else {
                                data.field.onChange({
                                  to: data.field.value?.to,
                                  object: data.field.value?.object,
                                  from: option?.value,
                                  fieldType: data.field.value?.fieldType,
                                });
                              }
                            }}
                          />
                        </HiddenFormLabel>
                      )}
                      <Column gap={2} fontSize="xl" color="text.secondary">
                        <ArrowRightIcon />
                      </Column>
                      <Column>
                        {options || creatable ? (
                          creatable ? (
                            <Column flex={1} gap={2} position="relative">
                              <HiddenFormLabel
                                label={`Select or enter a field from ${destinationDefinition?.name}`}
                              >
                                <Combobox
                                  supportsCreatableOptions
                                  createOptionMessage={(inputValue) =>
                                    `Create field "${inputValue}"`
                                  }
                                  emptyOptionsMessage="Type in a field name and Hightouch will create that field in the destination"
                                  isDisabled={
                                    isDisabled ||
                                    permission.unauthorized ||
                                    isMappingRequired
                                  }
                                  isInvalid={getIsValid({
                                    errors,
                                    index,
                                    mappingDirection: "to",
                                    mappingName: name,
                                  })}
                                  isOptionDisabled={(option) => {
                                    // For some reason when option is selected, it becomes disabled
                                    // and has `disabled = true` property, so we need to take this into account
                                    // and make sure selected option can't be disabled
                                    const isSelectedOption =
                                      destinationOptionKey(option) ===
                                      selectedOptionKey;
                                    if (isSelectedOption) {
                                      return false;
                                    }

                                    return Boolean(option.disabled);
                                  }}
                                  optionAccessories={(option) => {
                                    return option?.extendedType?.type
                                      ? {
                                          leftAccessory: {
                                            type: "icon",
                                            icon: NEW_ICON_MAP[
                                              option.extendedType.type
                                            ],
                                          },
                                        }
                                      : undefined;
                                  }}
                                  optionLabel={(option) => {
                                    return option.object?.label
                                      ? `${option.label} (${option.object.label})`
                                      : option.label;
                                  }}
                                  optionValue={destinationOptionKey}
                                  options={allDestinationOptions}
                                  placeholder={
                                    data.field.value?.to
                                      ? ""
                                      : `Select or enter a field from ${destinationDefinition?.name}`
                                  }
                                  value={selectedOptionKey}
                                  width="100%"
                                  onChange={(value) =>
                                    handleOptionChange({
                                      destinationFieldOptions,
                                      data,
                                      value,
                                    })
                                  }
                                  onCreateOption={(inputValue) => {
                                    data.field.onChange({
                                      ...data.field.value,
                                      to: inputValue || undefined,
                                      // Clear any object from a prior selection so
                                      // the custom value's composite key resolves.
                                      object: undefined,
                                    });
                                  }}
                                />
                              </HiddenFormLabel>
                              {creatableTypes && data.field.value?.to && (
                                <TypeSelect
                                  isDisabled={
                                    isDisabled || permission.unauthorized
                                  }
                                  options={creatableTypes}
                                  created={Boolean(
                                    options?.find(
                                      (option: Option) =>
                                        option.value === data.field.value?.to,
                                    ),
                                  )}
                                  placeholder="Select a field type..."
                                  value={data.field.value}
                                  width="100%"
                                  onTypeCreate={(value, typeOptions) =>
                                    handleOptionChange({
                                      destinationFieldOptions: creatableTypes,
                                      data,
                                      value: value as any,
                                      isCreatedOption: true,
                                      typeOptions,
                                    })
                                  }
                                />
                              )}
                            </Column>
                          ) : (
                            <HiddenFormLabel
                              label={`Select a field from ${destinationDefinition?.name}`}
                            >
                              <Combobox
                                isDisabled={
                                  isMatchboosterField ||
                                  isDisabled ||
                                  permission.unauthorized ||
                                  isMappingRequired
                                }
                                isInvalid={getIsValid({
                                  errors,
                                  index,
                                  mappingDirection: "to",
                                  mappingName: name,
                                })}
                                isOptionDisabled={(option) => {
                                  // For some reason when option is selected, it becomes disabled
                                  // and has `disabled = true` property, so we need to take this into account
                                  // and make sure selected option can't be disabled
                                  const isSelectedOption =
                                    destinationOptionKey(option) ===
                                    selectedOptionKey;
                                  if (isSelectedOption) {
                                    return false;
                                  }

                                  return Boolean(option.disabled);
                                }}
                                optionAccessories={(option) => {
                                  return option?.extendedType?.type
                                    ? {
                                        leftAccessory: {
                                          type: "icon",
                                          icon: NEW_ICON_MAP[
                                            option.extendedType.type
                                          ],
                                        },
                                      }
                                    : undefined;
                                }}
                                optionLabel={(option) => {
                                  return option.object?.label
                                    ? `${option.label} (${option.object.label})`
                                    : option.label;
                                }}
                                optionValue={destinationOptionKey}
                                options={allDestinationOptions}
                                placeholder={
                                  data.field.value?.to
                                    ? ""
                                    : `Select a field from ${destinationDefinition?.name}`
                                }
                                value={selectedOptionKey}
                                width="100%"
                                onChange={(value) =>
                                  handleOptionChange({
                                    destinationFieldOptions,
                                    data,
                                    value,
                                  })
                                }
                              />
                            </HiddenFormLabel>
                          )
                        ) : (
                          <Box width="100%">
                            <HiddenFormLabel
                              label={`Enter a field from ${destinationDefinition?.name}`}
                            >
                              <TextInput
                                isDisabled={
                                  isDisabled || permission.unauthorized
                                }
                                isInvalid={getIsValid({
                                  errors,
                                  index,
                                  mappingDirection: "to",
                                  mappingName: name,
                                })}
                                placeholder={`Enter a field from ${destinationDefinition?.name}`}
                                value={data.field.value?.to ?? ""}
                                width="100%"
                                onChange={(event) => {
                                  data.field.onChange({
                                    ...data.field.value,
                                    to: event.target.value,
                                  });
                                }}
                              />
                            </HiddenFormLabel>
                          </Box>
                        )}
                        {selectedOption?.extendedType?.type === "REFERENCE" &&
                          (referenceObjectOptions?.length ?? 0) > 1 && (
                            <Row alignItems="center" mt={2}>
                              <Text
                                color="gray.400"
                                fontWeight="semibold"
                                mr={2}
                                size="sm"
                                whiteSpace="nowrap"
                              >
                                Linked to:
                              </Text>
                              <HiddenFormLabel label="Select a linked object">
                                <Select
                                  isDisabled={
                                    isDisabled || permission.unauthorized
                                  }
                                  isInvalid={getIsValid({
                                    errors,
                                    index,
                                    mappingDirection: "to",
                                    mappingName: name,
                                  })}
                                  options={referenceObjectOptions ?? []}
                                  value={data.field.value?.lookup?.object}
                                  onChange={(selected) => {
                                    data.field.onChange({
                                      ...data.field.value,
                                      lookup: {
                                        by: null,
                                        byType: null,
                                        from: undefined,
                                        object: selected,
                                      },
                                    });
                                  }}
                                />
                              </HiddenFormLabel>
                            </Row>
                          )}
                      </Column>
                      <SyncType
                        isDisabled={
                          isDisabled ||
                          permission.unauthorized ||
                          !advanced ||
                          (allowIgnoreNullForAssociations
                            ? ["array", "object"]
                            : ["array", "object", "reference"]
                          ).includes(data.field.value?.type)
                        }
                        setValue={data.field.onChange}
                        supportOnlyWriteIfNull={supportOnlyWriteIfNull}
                        value={data.field.value}
                      />

                      {required && controlledFields.length < 1 ? (
                        // needs to exist to satisfy grid
                        <Row width="32px" />
                      ) : isMappingRequired ? (
                        // needs to exist at same width of 'X' icon to satisfy grid;
                        <Row
                          alignItems="center"
                          height="20px"
                          justifyContent="center"
                          width="32px"
                        >
                          <Tooltip message="Required">
                            <Box color="danger.500" fontSize={18}>
                              *
                            </Box>
                          </Tooltip>
                        </Row>
                      ) : (
                        (!isMatchboosterField ||
                          (isMatchboosterField &&
                            selectedOption?.extendedType
                              ?.matchBoosterOptional === true)) && (
                          <IconButton
                            aria-label="Remove mapping"
                            icon={CloseIcon}
                            isDisabled={isDisabled || permission.unauthorized}
                            onClick={() => {
                              remove(index);
                              clearMappingErrors(errors, [index], setErrors);
                            }}
                          />
                        )
                      )}

                      {retrieveErrorMessage(errors, index, name) && (
                        <Box gridColumn="1 / 4" color="danger.base">
                          {retrieveErrorMessage(errors, index, name)}
                        </Box>
                      )}

                      {data.field.value?.type === "object" &&
                        enableInLineMapper &&
                        appEnableInLineMapper && (
                          <Box gridColumn="1 / 4">
                            <InlineMapper
                              isDisabled={isDisabled || permission.unauthorized}
                              currentDepth={1}
                              enabledNestedInlineMapper={
                                enableInLineMapper && appEnableInLineMapper
                              }
                              errorPrefix={`${name}[${index}].from`}
                              jsonColumnProperties={jsonColumnProperties}
                              retrieveErrorMessage={retrieveErrorMessage}
                              selectedOptionContext={optionContext}
                              supportOnlyWriteIfNull={supportOnlyWriteIfNull}
                              templates={templates ?? []}
                              type="object"
                              value={data.field.value}
                              onChange={(value) => {
                                data.field.onChange({
                                  ...data.field.value,
                                  from: value,
                                  type: MappingType.OBJECT,
                                });
                              }}
                              onChangeJsonColumnProperties={
                                setJsonColumnProperties
                              }
                              onReloadEligibleInlineMapperColumns={
                                loadEligibleInlineMapperColumns
                              }
                            />
                          </Box>
                        )}

                      {data.field.value?.type === "array" &&
                        enableInLineMapper &&
                        appEnableInLineMapper &&
                        data.field.value?.from && (
                          <Box gridColumn="1 / 4">
                            <ArrayInlineMapper
                              isDisabled={isDisabled || permission.unauthorized}
                              currentDepth={1}
                              enabledNestedInlineMapper={
                                enableInLineMapper && appEnableInLineMapper
                              }
                              errorPrefix={`${name}[${index}].children`}
                              jsonColumnProperties={jsonColumnProperties}
                              retrieveErrorMessage={retrieveErrorMessage}
                              selectedOptionContext={optionContext}
                              templates={templates ?? []}
                              value={data.field.value}
                              onChange={(value) => {
                                data.field.onChange({
                                  ...data.field.value,
                                  children: value,
                                  type: MappingType.ARRAY,
                                });
                              }}
                              onChangeJsonColumnProperties={
                                setJsonColumnProperties
                              }
                              onReloadEligibleInlineMapperColumns={
                                loadEligibleInlineMapperColumns
                              }
                            />
                          </Box>
                        )}
                    </Box>
                  );
                }}
              />
            </DraggableItem>
          );
        })}
      </Reorder.Group>
      <ButtonGroup>
        <Button
          isDisabled={isDisabled || permission.unauthorized}
          onClick={() => {
            append({ type: "standard" });
          }}
        >
          Add mapping
        </Button>

        {(model || columns.length > 0) && (
          <Tooltip
            isDisabled={
              isDisabled ||
              permission.unauthorized ||
              watchAutoSyncColumns ||
              loading
            }
            message={
              isSuggested
                ? "All possible suggestions applied"
                : "Suggest mappings"
            }
          >
            <IconButton
              aria-label="Suggest mappings"
              icon={WandIcon}
              isDisabled={
                isDisabled ||
                permission.unauthorized ||
                watchAutoSyncColumns ||
                loading
              }
              variant="secondary"
              onClick={() => {
                append(suggestColumns());
              }}
            />
          </Tooltip>
        )}
      </ButtonGroup>
      {spread && (
        <SpreadColumnsSection
          name={name}
          columnOptionGroups={columnOptionGroups}
          isDisabled={
            isDisabled || permission.unauthorized || watchAutoSyncColumns
          }
          sourceName={sourceDefinition?.name}
        />
      )}
    </Column>
  );
};

const SpreadColumnsSection: FC<{
  name: string;
  columnOptionGroups: { label: string; options: ColumnOption[] }[];
  isDisabled: boolean;
  sourceName?: string;
}> = ({ name, columnOptionGroups, isDisabled, sourceName }) => {
  const spreadName = spreadFieldName(name);
  const { fields, append, remove, move } = useFieldArray({ name: spreadName });
  const [movedItemId, setMovedItemId] = useState<string | null>(null);
  const placeholder = `Select a JSON column from ${sourceName ?? "the source"}`;

  // Spread columns are applied in list order with last-wins on overlapping keys,
  // so reordering here controls which column takes precedence on a collision.
  const reorder = (updated: { id: string }[]): void => {
    const startIndex = fields.findIndex((f) => f.id === movedItemId);
    const destinationIndex = updated.findIndex((f) => f.id === movedItemId);
    if (startIndex === -1 || destinationIndex === -1) return;
    move(startIndex, destinationIndex);
  };

  return (
    <Column gap={2} mt={4} aria-label={`${spreadName} spread columns`}>
      <Column gap={1}>
        <Text fontWeight="medium">Spread JSON columns into fields</Text>
        <Text size="sm" color="text.secondary">
          Already have a column that holds the values to map? Select it here —
          every key in the column's JSON object becomes its own field, and
          mappings above take precedence. For consistent results, make sure
          every row's JSON object has the same set of keys. Optionally apply
          transforms to each key before it's sent (e.g. remove special
          characters so warehouse column names match the destination's API field
          names).
        </Text>
      </Column>
      <Reorder.Group
        layoutScroll
        style={{
          listStyle: "none",
          paddingLeft: 0,
          position: "relative",
          display: "grid",
          gridGap: "var(--chakra-space-1)",
        }}
        values={fields}
        onReorder={reorder}
      >
        {fields.map((field, index) => {
          const isDragDisabled = isDisabled || fields.length === 1;
          return (
            <DraggableItem
              key={field.id}
              isDownDisabled={isDisabled || index === fields.length - 1}
              isDragDisabled={isDragDisabled}
              isUpDisabled={isDisabled || index === 0}
              item={field}
              onDown={() => move(index, index + 1)}
              onDragEnd={() => setMovedItemId(null)}
              onDragStart={(id) => setMovedItemId(id)}
              onUp={() => move(index, index - 1)}
            >
              <Box
                display="grid"
                gridTemplateColumns="minmax(0, 1fr) 24px 24px minmax(0, 1fr)"
                alignItems="center"
                gap={1}
                width="100%"
              >
                <Controller
                  name={`${spreadName}.${index}.from`}
                  render={({ field: f }) => (
                    <HiddenFormLabel label={placeholder}>
                      <GroupedCombobox
                        isDisabled={isDisabled}
                        optionAccessories={(option) =>
                          option?.value?.["type"] ===
                          "additionalColumnReference"
                            ? {
                                leftAccessory: {
                                  type: "icon",
                                  icon: TraitIcon,
                                },
                              }
                            : undefined
                        }
                        optionGroups={columnOptionGroups}
                        placeholder={placeholder}
                        value={f.value}
                        width="100%"
                        onChange={(value) => f.onChange(value || undefined)}
                      />
                    </HiddenFormLabel>
                  )}
                />
                <SpreadTransformsButton
                  isDisabled={isDisabled}
                  namePrefix={`${spreadName}.${index}`}
                />
                <IconButton
                  aria-label="Remove JSON column"
                  icon={CloseIcon}
                  isDisabled={isDisabled}
                  onClick={() => remove(index)}
                />
              </Box>
            </DraggableItem>
          );
        })}
      </Reorder.Group>
      <Box>
        <Button
          isDisabled={isDisabled}
          onClick={() =>
            append({ type: MappingType.SPREAD, from: undefined, to: "" })
          }
        >
          Add JSON column
        </Button>
      </Box>
    </Column>
  );
};

// Popover surface(s) that should NOT dismiss the transform popover on click:
// its own content, the trigger, and any portaled dropdown menu the Select
// renders into the body.
const SPREAD_TRANSFORMS_SURFACE = "[data-spread-transforms-surface]";
const OPEN_POPOVER_TRIGGER = '[aria-haspopup="dialog"][aria-expanded="true"]';
const PORTALED_MENU = '[role="listbox"], [role="option"]';

// Per-row settings cog for a spread column's key/value transforms — mirrors the
// SyncType cog used on regular mapping rows. The transforms are hidden behind
// the cog (with a dot when either is set) so the spread section stays clean by
// default. One transform per side (a Select; Custom Liquid reveals a template
// box, Extract reveals a path box). Dismissal uses a manual pointerdown check
// rather than Chakra's focus-based `closeOnBlur`: editing the popover (picking
// from the Select, typing a template) drops focus to <body> between steps, which
// breaks closeOnBlur and leaves the popover stuck open. Same as MergeTagPopover.
const SpreadTransformsButton: FC<{
  isDisabled: boolean;
  namePrefix: string;
}> = ({ isDisabled, namePrefix }) => {
  // Two independent field subscriptions via hooks (NOT nested <Controller>s —
  // nesting re-instantiates the inner controller on every outer render and
  // desyncs its field, which broke clearing once both transforms existed).
  const { field: keyField } = useController({
    name: `${namePrefix}.keyTransform`,
  });
  const { field: valueField } = useController({
    name: `${namePrefix}.valueTransform`,
  });
  const keyTransform =
    (keyField.value as SpreadTransform | undefined) ?? undefined;
  const valueTransform =
    (valueField.value as SpreadTransform | undefined) ?? undefined;
  const onChangeKey = (next: SpreadTransform | undefined) =>
    keyField.onChange(next ?? undefined);
  const onChangeValue = (next: SpreadTransform | undefined) =>
    valueField.onChange(next ?? undefined);

  const { isOpen, onClose, onToggle } = useDisclosure();

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(SPREAD_TRANSFORMS_SURFACE)) return;
      if (target.closest(OPEN_POPOVER_TRIGGER)) return;
      if (target.closest(PORTALED_MENU)) return;
      onClose();
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [isOpen, onClose]);

  return (
    <Popover
      closeOnBlur={false}
      isLazy
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-start"
      returnFocusOnClose={false}
    >
      <Box pos="relative">
        <PopoverTrigger>
          <IconButton
            aria-label="Configure key and value transforms"
            icon={SettingsIcon}
            isDisabled={isDisabled}
            onClick={onToggle}
          />
        </PopoverTrigger>
        {(keyTransform || valueTransform) && (
          <Box position="absolute" right={0} top={1} zIndex={1}>
            <Box
              background="primary.base"
              borderRadius="full"
              height={2}
              width={2}
            />
          </Box>
        )}
      </Box>
      <Portal>
        <PopoverContent data-spread-transforms-surface="" p={2} width="320px">
          <Column gap={4}>
            <SpreadTransformSection
              description="Applied to every key in this column before it's sent."
              isDisabled={isDisabled}
              side="key"
              title="Key transform"
              value={keyTransform}
              onChange={onChangeKey}
            />
            <SpreadTransformSection
              description="Applied to every value in this column before it's sent."
              isDisabled={isDisabled}
              side="value"
              title="Value transform"
              value={valueTransform}
              onChange={onChangeValue}
            />
          </Column>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

// Secondary helper text that mirrors the mapper-row cog's Radio *description*
// typography (font size sm at lineHeights-5). The design-system `Text` pins
// size="sm" to lineHeights-4 and rejects a lineHeight override, so use `Box`
// (the escape hatch) with theme tokens to match the Radio look exactly.
const SpreadHelpText: FC<PropsWithChildren> = ({ children }) => (
  <Box color="text.secondary" fontSize="sm" lineHeight={5}>
    {children}
  </Box>
);

// One transform picker (key or value). The options shown are filtered to those
// valid on `side`, so e.g. "Extract from object" only appears on the value side.
const SpreadTransformSection: FC<{
  isDisabled: boolean;
  side: SpreadTransformSide;
  title: string;
  description: string;
  value: SpreadTransform | undefined;
  onChange: (value: SpreadTransform | undefined) => void;
}> = ({ isDisabled, side, title, description, value, onChange }) => {
  const selectType = (type: SpreadTransformType | undefined) => {
    if (!type) {
      onChange(undefined);
    } else if (type === "liquid") {
      // Preserve an already-typed template when re-selecting Liquid.
      onChange({
        type: "liquid",
        template: value?.type === "liquid" ? value.template : "",
      });
    } else if (type === "extract") {
      // Preserve an already-typed path when re-selecting Extract.
      onChange({
        type: "extract",
        path: value?.type === "extract" ? value.path : "",
      });
    } else {
      onChange({ type });
    }
  };

  return (
    <Column gap={2}>
      <Column gap={0.5}>
        <Text fontWeight="semibold">{title}</Text>
        <SpreadHelpText>{description}</SpreadHelpText>
      </Column>
      <Select
        isClearable
        isDisabled={isDisabled}
        options={spreadTransformOptions(side)}
        placeholder="No transform"
        size="sm"
        value={value?.type}
        width="100%"
        onChange={(type) => selectType(type ?? undefined)}
      />
      {value?.type === "liquid" && (
        <Column gap={1}>
          <Textarea
            isDisabled={isDisabled}
            placeholder={
              side === "key"
                ? '{{ key | remove: "_" | downcase }}'
                : "{{ value.value }}"
            }
            rows={3}
            value={value.template}
            width="100%"
            onChange={(event) =>
              onChange({ type: "liquid", template: event.target.value })
            }
          />
          <SpreadHelpText>
            Use {"{{ key }}"} and {"{{ value }}"} for the raw key and value; the
            rendered string becomes the{" "}
            {side === "key" ? "field name" : "value"}.
          </SpreadHelpText>
        </Column>
      )}
      {value?.type === "extract" && (
        <Column gap={1}>
          <TextInput
            isDisabled={isDisabled}
            placeholder="value"
            size="sm"
            value={value.path}
            width="100%"
            onChange={(event) =>
              onChange({ type: "extract", path: event.target.value })
            }
          />
          <SpreadHelpText>
            Dot-path to the field to pull out of each object value (e.g.{" "}
            {"value"} or {"data.value"}); its original type is preserved.
          </SpreadHelpText>
        </Column>
      )}
    </Column>
  );
};

type DraggableItemProps = {
  isDownDisabled?: boolean;
  isDragDisabled?: boolean;
  isUpDisabled?: boolean;
  item: Record<"id", string>;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onUp: () => void;
  onDown: () => void;
};

const DraggableItem: FC<PropsWithChildren<DraggableItemProps>> = ({
  children,
  isDownDisabled = false,
  isDragDisabled = false,
  isUpDisabled = false,
  item,
  onDragStart,
  onDragEnd,
  onUp,
  onDown,
}) => {
  const [dragging, setDragging] = useState(false);
  const controls = useDragControls();

  return (
    <Reorder.Item
      dragListener={false}
      dragControls={controls}
      value={item}
      style={{
        display: "grid",
        gridAutoFlow: "column",
        gridTemplateColumns: "auto 1fr",
        columnGap: "5px",
        userSelect: "none",
        borderRadius: "var(--chakra-radii-md)",
        backgroundColor: "rgba(255,255,255,0.85)",
        paddingBottom: "4px",
        paddingTop: "4px",
      }}
      onDragStart={() => {
        if (isDragDisabled) {
          return;
        }
        onDragStart(item.id);
        setDragging(true);
      }}
      onDragEnd={() => {
        if (isDragDisabled) {
          return;
        }
        onDragEnd();
        setDragging(false);
      }}
      whileDrag={{
        paddingRight: "8px",
        width: "calc(100% + 8px)",
        boxShadow:
          "0px 12px 16px rgba(16, 24, 40, 0.16), 0px 8px 16px rgba(16, 24, 40, 0.16), 0px 0px 12px rgba(16, 24, 40, 0.08)",
      }}
      transition={{ duration: 0 }}
    >
      <Box
        style={{
          display: "grid",
          gridAutoFlow: "column",
          gridTemplateAreas: `"handle up" "handle down"`,
          gridTemplateColumns: "auto auto 1fr",
          gap: "4px",
          height: "2.5em",
        }}
      >
        <Box
          aria-label="Drag handle"
          cursor={isDragDisabled ? "default" : dragging ? "grabbing" : "grab"}
          color={isDragDisabled ? "base.border" : "inherit"}
          className="handle"
          onPointerDown={(e) => {
            if (!isDragDisabled) {
              controls.start(e);
            }
          }}
          as={DragIcon}
          fontSize="3xl"
          style={{ gridArea: "handle", alignSelf: "center" }}
        />
        <Box
          aria-label="Move up"
          as="button"
          cursor={isUpDisabled ? "default" : "pointer"}
          color={isUpDisabled ? "base.border" : "inherit"}
          disabled={isUpDisabled}
          fontSize="3xl"
          borderRadius="sm"
          _focus={{ outline: "none" }}
          _focusVisible={{ boxShadow: "outline" }}
          style={{ gridArea: "up", alignContent: "start", lineHeight: "0" }}
          onClick={onUp}
        >
          <Box as={ChevronUpIcon} width="0.6em" height="0.6em" />
        </Box>
        <Box
          aria-label="Move down"
          as="button"
          cursor={isDownDisabled ? "default" : "pointer"}
          color={isDownDisabled ? "base.border" : "inherit"}
          disabled={isDownDisabled}
          fontSize="3xl"
          borderRadius="sm"
          _focus={{ outline: "none" }}
          _focusVisible={{ boxShadow: "outline" }}
          style={{
            gridArea: "down",
            alignContent: "end",
            lineHeight: "0",
          }}
          onClick={onDown}
        >
          <Box as={ChevronDownIcon} width="0.6em" height="0.6em" />
        </Box>
      </Box>
      {children}
    </Reorder.Item>
  );
};

const isTraitEnrichment = (value: unknown): boolean => {
  return get(value, "type") === "additionalColumnReference";
};

const isTraitLike = (value: unknown): boolean => {
  return isTraitEnrichment(value) || get(value, "column.type") === "trait";
};

export const formatFromColumnOption = (option) => {
  if (isTraitLike(option.value)) {
    return (
      <Row sx={{ alignItems: "center", gap: 1 }}>
        <Box as={TraitIcon} color="text.secondary" boxSize={4} />
        <Text>{option.label}</Text>
        {isTraitEnrichment(option.value) && (
          <Tooltip message="Audience-specific trait enrichment">
            <Box as={AudienceIcon} color="text.secondary" boxSize={4} />
          </Tooltip>
        )}
      </Row>
    );
  }

  return option?.label;
};

export const extendedTypeToFormkitOptions = (
  extendedOption: ExtendedOption | undefined,
): SelectedOptionContext[] | undefined => {
  let childProperties: { [key: string]: ExtendedTypesSync } | undefined;

  if (!extendedOption) return undefined;

  if (
    extendedOption.extendedType?.type === "OBJECT" &&
    extendedOption.extendedType?.properties
  ) {
    childProperties = extendedOption.extendedType.properties;
  }

  if (
    extendedOption.extendedType?.type === "ARRAY" &&
    extendedOption.extendedType?.items &&
    extendedOption.extendedType?.items?.type === "OBJECT"
  ) {
    childProperties = extendedOption.extendedType.items?.properties;
  }

  if (!childProperties) return undefined;

  const options: SelectedOptionContext[] = [];

  for (const [key, value] of Object.entries(childProperties)) {
    if (value.type === "OBJECT" && value.properties) {
      const nestedOption: SelectedOptionContext[] = [];

      for (const [nestedKey, nestedValue] of Object.entries(value.properties)) {
        nestedOption.push({
          value: nestedKey,
          label: nestedValue.label ?? nestedKey,
          required: nestedValue.required,
          type: nestedValue.type,
        });
      }

      options.push({
        value: key,
        label: value.label ?? key,
        required: value.required,
        type: value.type,
        properties: nestedOption,
      });
    } else {
      options.push({
        value: key,
        label: value.label ?? key,
        required: value.required,
        type: value.type,
      });
    }
  }

  return options;
};
